"use client";

import { Fragment, MutableRefObject, useState } from "react";
import { useRouter } from "next/navigation";
import { Dialog, Transition } from "@headlessui/react";
import { FaHeadset } from "react-icons/fa";
import { GiCharacter } from "react-icons/gi";
import { Character } from "schema";

import { trpc } from "~/app/providers";
import { LineBucket } from "../page";
import AddTagModal from "./AddTagModal";
import SelectDropDown from "./CharacterSelect";

export default function EditLineModal({
  characters,
  stageHands,
  lineBucket,
  close,
}: {
  characters: Character[];
  stageHands: Character[];
  lineBucket: LineBucket;
  close: (
    focusableElement?: HTMLElement | MutableRefObject<HTMLElement | null>,
  ) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [lineVersion, setLineVersion] = useState(lineBucket.versions);
  const [activeVersion, setActiveVersion] = useState(lineBucket.lineId);
  const initTab = ["Scene", "Stage Direction", "Act"].includes(
    lineBucket.characterName,
  )
    ? 2
    : 1;
  const [activeTab, setActiveTab] = useState(initTab);

  const handleTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    index: number,
  ) => {
    setLineVersion((prevState) => {
      const updatedVersions = [...prevState];
      updatedVersions[index].text = e.target.value;
      return updatedVersions;
    });
  };

  const handleCharacterChange = (newCharacter: Character) => {
    setLineVersion((prevState) =>
      prevState.map((version) => ({
        ...version,
        characterId: newCharacter.id,
      })),
    );
  };

  const router = useRouter();

  function closeModal() {
    setIsOpen(false);
    close();
  }

  function openModal() {
    setIsOpen(true);
  }

  const updateLine = trpc.lines.updateLine.useMutation({
    onSuccess: () => {
      router.refresh();
      closeModal();
    },
  });

  const setCurrentLine = trpc.lines.setCurrentLine.useMutation({
    onSuccess: () => {
      router.refresh();
      closeModal();
    },
  });

  const deleteTag = trpc.lines.deleteTag.useMutation({
    onSuccess: () => {
      router.refresh();
    },
  });

  const addVersion = () => {
    setLineVersion([
      ...lineVersion,
      {
        id: -1,
        playId: characters[0].playId,
        text: "",
        characterId: lineBucket.characterId,
        bucket: lineBucket.id,
        created_at: null,
      },
    ]);
  };

  return (
    <>
      <button
        className="flex w-full items-center gap-1 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
        onClick={openModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="size-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
          />
        </svg>
        Edit
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center align-middle">
                    {activeTab === 1 && (
                      <div>
                        <label className="block text-lg font-medium text-gray-800">
                          Characters
                        </label>
                        <SelectDropDown
                          characters={characters}
                          onChange={handleCharacterChange}
                          newLine={false}
                          line={lineBucket}
                        />
                      </div>
                    )}
                    {activeTab === 2 && (
                      <div>
                        <label className="block text-lg font-medium text-gray-800">
                          Stage Direction
                        </label>
                        <SelectDropDown
                          characters={stageHands}
                          onChange={handleCharacterChange}
                          newLine={false}
                          line={lineBucket}
                        />
                      </div>
                    )}
                    <div className="ml-10 mr-10 flex flex-col gap-4">
                      <button
                        className={activeTab === 1 ? "text-amber-600" : ""}
                        onClick={() => setActiveTab(1)}
                      >
                        <GiCharacter className="h-6 w-6" />
                      </button>
                      <button
                        className={activeTab === 2 ? "text-amber-600" : ""}
                        onClick={() => setActiveTab(2)}
                      >
                        <FaHeadset className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-8">
                    <div>
                      <div className="my-2 flex items-center gap-2">
                        {lineBucket.tags
                          ? lineBucket.tags.map((item, idx) => (
                              <span
                                key={idx}
                                className={`inline-flex items-center justify-center rounded-full bg-${item.color}-100 px-2.5 py-0.5 text-${item.color}-700`}
                              >
                                <p className="whitespace-nowrap text-sm">
                                  {item.text}
                                </p>

                                <button
                                  onClick={() =>
                                    deleteTag.mutate({ tagId: item.id })
                                  }
                                  className={`bg-${item.color}-200 -me-1 ms-1.5 inline-block rounded-full p-0.5 text-${item.color}-700 transition hover:bg-${item.color}-300`}
                                >
                                  <span className="sr-only">Remove badge</span>

                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="h-3 w-3"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              </span>
                            ))
                          : null}
                        <AddTagModal line={lineBucket} />
                      </div>
                      <div className="hidden sm:block">
                        <div className="border-b border-gray-200">
                          <nav className="-mb-px flex gap-6" aria-label="Tabs">
                            {lineVersion.map((item, idx) => {
                              return (
                                <div key={idx}>
                                  {" "}
                                  <button
                                    className={
                                      activeVersion === item.id
                                        ? "shrink-0 border-b-2 border-slate-400 px-1 pb-4 text-sm font-medium text-gray-600"
                                        : "shrink-0 border-b-2 border-transparent px-1 pb-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                    }
                                    onClick={() => setActiveVersion(item.id)}
                                  >
                                    Version {idx + 1}
                                  </button>
                                </div>
                              );
                            })}
                          </nav>
                        </div>
                      </div>
                    </div>
                    <label className="mt-5 block text-lg font-medium text-gray-800">
                      Line
                    </label>
                    <div>
                      {lineVersion.map((item, idx) => {
                        return (
                          <div key={idx}>
                            {activeVersion === item.id && (
                              <>
                                <textarea
                                  onChange={(e) => handleTextChange(e, idx)}
                                  className="mt-2 w-full rounded-lg border-gray-200 align-top shadow-sm focus-within:border-slate-300 focus-within:ring-slate-300 sm:text-sm"
                                  rows={4}
                                  defaultValue={item.text}
                                ></textarea>
                              </>
                            )}
                          </div>
                        );
                      })}
                      <div className="mr-4 mt-6 flex justify-between">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                          onClick={addVersion}
                        >
                          New Version
                        </button>
                        <div className="flex justify-end gap-4">
                          <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            onClick={() => {
                              updateLine.mutate(
                                lineVersion.map((item) => {
                                  return {
                                    id: item.id,
                                    text: item.text,
                                    characterId: item.characterId,
                                    bucket: item.bucket,
                                    playId: item.playId,
                                  };
                                }),
                              );
                            }}
                          >
                            Update
                          </button>
                          <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
                            onClick={() =>
                              setCurrentLine.mutate({
                                bucketId: lineBucket.id,
                                currentLine: activeVersion,
                              })
                            }
                          >
                            Finalize
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
